import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildExtractionKey } from '../../../../algorithms/shoreS';
import { Actions as ShoreSAnalysis } from '../../../../store/ducks/shoreSAnalysis';
import { readSelectedImages } from './utils';

/**
 * @function useShoreSReferenceExtraction
 * @description Kicks off the reference-coastline extraction without waiting on the user's
 * baseline or transects. Dispatches only once the caller says it is `enabled` and the model bbox,
 * the mission and the selected images are all available, and passes a `buildExtractionKey`
 * fingerprint so a cached run with matching inputs is reused instead of redone.
 *
 * The `enabled` gate exists because the extraction is heavy enough to starve the Google Maps
 * initialization if both start together, leaving the user staring at a blank map that looks
 * frozen. The processing page holds it back until the map has loaded.
 * @param {boolean} [enabled] - Whether the extraction may start yet.
 * @see {@link buildExtractionKey} - Builds the fingerprint that gates the re-run.
 * **Called by:
 * @see {ProcessingShoreSPage} - once its map has loaded.
 * @memberof module:ShoreS
 * @returns {Object} The `extraction` sub-state, for rendering status/progress.
 */
export const useShoreSReferenceExtraction = (enabled = true) => {
  const dispatch = useDispatch();

  const extraction = useSelector((state) => state.shoreSAnalysis.extraction);
  const satelliteName = useSelector((state) => state.acquisition.satellite?.name);
  const crs = useSelector((state) => state.shoreSModel.crs);
  const modelBBox = useSelector((state) => state.shoreSModel.modelBBox);


  const selectedImages = useMemo(() => readSelectedImages(), []);

  const key = useMemo(
    () => buildExtractionKey({ selectedImages, satelliteName, crs, modelBBox }),
    [selectedImages, satelliteName, crs, modelBBox],
  );

  const ready = Boolean(enabled && modelBBox && satelliteName && selectedImages.length);

  useEffect(() => {
    if (!ready) return;
    dispatch(ShoreSAnalysis.extractReferences(selectedImages, key));

  }, [ready, key, dispatch]);

  return extraction;
};

export default useShoreSReferenceExtraction;
